import React, { useState } from 'react';
import { OrgBadge, teamName } from '../../components/PlacementsTable';
import { fmtDate, fmtMoney, fmtNumber, usePagedSimple } from '../../hooks';
import { Purpose, Alert, Card, Chip, Progress, SearchBox, Skeleton, Stat } from '../../ui/basics';
import { DataTable } from '../../ui/DataTable';

const WINDOWS = [30, 60, 90, 180];

export default function Bench({ org, lead }) {
  const [days, setDays] = useState(60);
  const [search, setSearch] = useState('');
  const paged = usePagedSimple('analytics/bench/', { organization: org, lead, days, search }, 10);
  const { data, error } = paged;
  const at = (w) => data && data.replacement.find((x) => x.within === w);
  const cols = [
    { key: 'candidate_name', label: 'Candidate', render: (r) => <b>{r.candidate_name}</b> },
    ...(org === 'combined' ? [{ key: 'organization', label: 'Organization', render: (r) => <OrgBadge name={r.organization} /> }] : []),
    { key: 'client', label: 'Last client' }, { key: 'recruiter', label: 'Recruiter' }, { key: 'lead', label: 'Team', render: (r) => teamName(r.lead) },
    { key: 'ended', label: 'Ended', render: (r) => fmtDate(r.ended) },
    { key: 'days_idle', label: 'Idle', align: 'right', render: (r) => `${r.days_idle} days` },
    { key: 'tenure_days', label: 'Last stint', align: 'right', render: (r) => `${r.tenure_days} days` },
    { key: 'gross_margin', label: 'Margin / hr', align: 'right', render: (r) => (r.gross_margin === null ? '—' : fmtMoney(r.gross_margin)) },
    { key: 'placements', label: 'Placements so far', align: 'right' },
  ];
  return (
    <div className="stack">
      <Purpose what="Helps redeploy people quickly: lists candidates whose last placement just ended and who have nothing active, and shows how often and how fast people get placed again." use="Choose how recently they ended (freshest are easiest to place), search by name, client or recruiter, and hand the list to the recruiters." care="Candidates are matched by name within an organization, so two people with the same name count as one." />
      <Card title="Bench and redeployment" subtitle="Candidates whose last placement ended recently and who have nothing active. Freshest first, since they are the easiest to place again."
        actions={<div className="row" style={{ gap: 6 }}>{WINDOWS.map((n) => <Chip key={n} on={n === days} onClick={() => setDays(n)}>Ended in last {n} days</Chip>)}</div>}>
        {error && <Alert>{error}</Alert>}
        {!data ? <Skeleton h={70} /> : (
          <div className="grid cols-4">
            <Stat label="On the bench" value={fmtNumber(data.bench_total)} sub={data.median_idle === null ? 'nobody' : `idle ${data.median_idle} days (median)`} />
            <Stat label="Placed again within 30 days" value={at(30).percent === null ? '—' : `${at(30).percent}%`} sub={`${fmtNumber(at(30).replaced)} of ${fmtNumber(at(30).ended)} that ended in the last year`} />
            <Stat label="Placed again within 90 days" value={at(90).percent === null ? '—' : `${at(90).percent}%`} sub={`${fmtNumber(at(90).replaced)} of ${fmtNumber(at(90).ended)}`} />
            <Stat label="Typical wait" value={data.median_gap === null ? '—' : `${data.median_gap} days`} sub="between placements, when placed again" />
          </div>
        )}
        {data && data.by_team.length > 0 && <div className="row" style={{ gap: 6, marginTop: 12 }}><span className="muted">Teams</span>{data.by_team.map((t) => <Chip key={t.name}>{teamName(t.name)} · {t.count}</Chip>)}</div>}
        {data && (
          <div className="grid cols-4" style={{ marginTop: 12 }}>
            {data.replacement.map((x) => (
              <div key={x.within}><div className="muted">Placed again within {x.within} days</div><Progress value={x.percent || 0} max={100} /></div>
            ))}
          </div>
        )}
      </Card>
      <Card flush>
        <div className="toolbar"><SearchBox value={search} onChange={setSearch} placeholder="Search candidate, client or recruiter…" /></div>
        <DataTable columns={cols} paged={paged} rowKey={(r) => r.s_no} noun="candidates" empty={<div className="empty"><strong>Nobody on the bench</strong>No candidate ended a placement in this window without a new one.</div>} />
        <div className="pager"><span>Candidates are matched by name within an organization, so two people with the same name would be treated as one.</span></div>
      </Card>
    </div>
  );
}
